
import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface NavbarProps {
  isDarkTheme: boolean;
  toggleTheme: () => void;
}

const Navbar = ({ isDarkTheme, toggleTheme }: NavbarProps) => {
  const { isAuthenticated, user, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMenuOpen(false); // Close menu after sign out
    } catch (error) {
      console.error("Failed to sign out:", error);
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/70 backdrop-blur-xl shadow-md border-b border-white/5' 
          : 'bg-transparent'
      } safe-area-top`}
    >
      <div className="container flex items-center justify-between h-20">
        {/* Logo - Mobile optimized height */}
        <NavLink to="/" className="flex items-center py-2">
          <img 
            alt="CHRONA Logo" 
            className="h-16 md:h-20 object-contain" 
            src="/lovable-uploads/67216cc1-3990-49e5-b703-b43fd4ee68c7.png" 
            loading="lazy"
          />
        </NavLink>

        {/* Desktop Navigation - Improved alignment and spacing */}
        <div className="hidden md:flex items-center justify-end space-x-2 flex-1">
          <NavbarLinks 
            isAuthenticated={isAuthenticated} 
            user={user} 
            onSignOut={handleSignOut} 
          />
        </div>

        {/* Improved Mobile Menu Trigger */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              className="md:hidden min-w-[48px] min-h-[48px] rounded-full mr-1 active:scale-95 transition-transform bg-chrona-background/50 backdrop-blur-md border border-white/10 hover:bg-white/10 touch-manipulation"
              aria-label="Menu"
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-5 h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </Button>
          </SheetTrigger>
          
          {/* Improved Mobile Menu */}
          <SheetContent 
            side="left" 
            className="bg-[#1A1A1A] border-r border-[#4C956C]/20 w-[85%] max-w-[300px] shadow-lg shadow-black/30"
          >
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center">
                <img 
                  src="/lovable-uploads/bbbd313b-d6bd-4b33-af58-258cd00a4dc3.png" 
                  alt="CHRONA" 
                  className="h-10 mr-2" 
                  loading="lazy"
                />
                <span>Navigation</span>
              </SheetTitle>
              <SheetDescription>
                Erkunde CHRONA und verwalte dein Konto.
              </SheetDescription>
            </SheetHeader>
            <div className="py-6 mt-2">
              <NavbarLinks 
                isAuthenticated={isAuthenticated} 
                user={user} 
                onSignOut={handleSignOut} 
                isMobile={true} 
                onClose={() => setIsMenuOpen(false)} 
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

interface NavbarLinksProps {
  isAuthenticated: boolean;
  user: any;
  onSignOut: () => void;
  isMobile?: boolean;
  onClose?: () => void;
}

const NavbarLinks = ({
  isAuthenticated,
  user,
  onSignOut,
  isMobile = false,
  onClose
}: NavbarLinksProps) => {
  const closeIfMobile = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };
  
  // Enhanced styles for a more polished appearance
  const linkBaseClasses = "flex items-center gap-1.5 py-3 md:py-2.5 w-full md:w-auto px-4 md:px-4 rounded-xl md:rounded-lg text-base md:text-sm transition-colors touch-manipulation min-h-[48px] group relative font-medium";
  const linkActiveClasses = "text-[#4C956C] bg-[#4C956C]/10 md:bg-transparent font-semibold";
  const linkInactiveClasses = "text-[#F8F8F8] hover:text-[#4C956C] hover:bg-[#4C956C]/5 md:hover:bg-transparent";
  
  // Improved underline animation with thicker line and smoother transition
  const UnderlineAnimation = () => (
    <span className="absolute left-0 bottom-0 w-full h-0.5 bg-[#4C956C] rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left hidden md:block" />
  );

  return (
    <ul className={`flex flex-col md:flex-row ${isMobile ? 'gap-2' : 'gap-2 md:gap-1 items-center justify-end'}`}>
      <li className="w-full md:w-auto">
        <NavLink 
          to="/" 
          className={({ isActive }) => 
            `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses}`
          }
          onClick={closeIfMobile}
        >
          Home
          <UnderlineAnimation />
        </NavLink>
      </li>
      <li className="w-full md:w-auto">
        <NavLink 
          to="/booking"
          className={({ isActive }) => 
            `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses}`
          }
          onClick={closeIfMobile}
        >
          Termin buchen
          <UnderlineAnimation />
        </NavLink>
      </li>
      
      {isAuthenticated ? (
        <>
          <li className="w-full md:w-auto">
            <NavLink 
              to="/dashboard" 
              className={({ isActive }) => 
                `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses}`
              }
              onClick={closeIfMobile}
            >
              Dashboard
              <UnderlineAnimation />
            </NavLink>
          </li>
          <li className="w-full md:w-auto">
            <NavLink 
              to="/subscriptions" 
              className={({ isActive }) => 
                `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses}`
              }
              onClick={closeIfMobile}
            >
              <CreditCard className="w-4 h-4" />
              <span>Abos</span>
              <UnderlineAnimation />
            </NavLink>
          </li>
          
          {/* Divider */}
          {isMobile && <li className="border-t border-white/10 my-2"></li>}
          
          <li className="w-full md:w-auto">
            <Button 
              variant="ghost" 
              onClick={onSignOut} 
              className={`${linkBaseClasses} justify-start md:justify-center text-[#F8F8F8] hover:text-[#4C956C] hover:bg-[#4C956C]/5 w-full text-left min-h-[48px]`}
            >
              Abmelden
              <UnderlineAnimation />
            </Button>
          </li>
          <li className="w-full md:w-auto ml-0 md:ml-2">
            <NavLink 
              to="/profile" 
              className={({ isActive }) => 
                `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses} md:px-3`
              }
              onClick={closeIfMobile}
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src="" />
                <AvatarFallback>{user?.email?.substring(0, 2).toUpperCase() || 'UN'}</AvatarFallback>
              </Avatar>
              <span className="ml-2 truncate md:hidden">{user?.email || "Profil"}</span>
            </NavLink>
          </li>
        </>
      ) : (
        <>
          {/* Divider */}
          {isMobile && <li className="border-t border-white/10 my-2"></li>}
          
          <li className="w-full md:w-auto">
            <NavLink 
              to="/login" 
              className={({ isActive }) => 
                `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses}`
              }
              onClick={closeIfMobile}
            >
              Anmelden
              <UnderlineAnimation />
            </NavLink>
          </li>
          <li className="w-full md:w-auto">
            <NavLink 
              to="/register" 
              className={({ isActive }) => 
                `${linkBaseClasses} ${isActive ? linkActiveClasses : linkInactiveClasses}`
              }
              onClick={closeIfMobile}
            >
              Registrieren
              <UnderlineAnimation />
            </NavLink>
          </li>
        </>
      )}
    </ul>
  );
};

export default Navbar;
